import type { FieldErrors } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import type { CardPaymentFormFields } from '@cencosud-cencommerce/paris-uikit';

import { useAppSelector } from '@/Application/Hooks/Redux';
import { selectBuyNowSelectedCard } from '@/Application/Selectors/buyNow/useBuyNowCart';
import { selectBuyNowPaymentMethodsData } from '@/Application/Selectors/buyNow/useBuyNowPaymentMethods';
import { URL_ROUTES } from '@/constants';
import { store } from '@/Infrastructure/State';
import { useBuyNowPaymentError } from '@/modules/buy-now/application/useBuyNowPaymentError';
import { createBuyNowOrderAsync } from '@/modules/buy-now/infrastructure/BuyNowReduxRepository';
import {
  setIsProcessingPayment,
  setIsSummaryOpen
} from '@/modules/buy-now/infrastructure/slices';
import type { IBuyNowSavedCardResponse } from '@/modules/buy-now/interfaces/IBuyNowSavedCardResponse';
import type { IErrorResponse } from '@/modules/buy-now/utils/error-handler/buy-now-error-handler.interfaces';
import { buildBuyNowPurchaseSearchParams } from '@/modules/buy-now/utils/purchase-search-params';
import { encryptData } from '@/modules/perseo-billing/utils/encrypt';

interface UsePaymentButtonReturn {
  isTermsChecked: boolean;
  submissionHandler: () => void;
  paymentForm: ReturnType<typeof useFormContext<CardPaymentFormFields>>;
}

export const usePaymentButton = (): UsePaymentButtonReturn => {
  const selectedCard = useAppSelector(selectBuyNowSelectedCard);
  const { cartId } = useParams<{ cartId: string }>();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const paymentForm = useFormContext<CardPaymentFormFields>();
  const paymentMethodsData = useAppSelector(selectBuyNowPaymentMethodsData);
  const { handleBuyNowPaymentError } = useBuyNowPaymentError();

  const { control, handleSubmit } = paymentForm;

  const handleSubmitForm = async (
    data: CardPaymentFormFields
  ): Promise<void> => {
    if (!cartId) {
      console.error('Missing cartId');
      return;
    }

    if (!selectedCard) {
      console.error('Missing selectedCard');
      return;
    }

    dispatch(setIsProcessingPayment(true));

    try {
      const savedCard = paymentMethodsData?.savedCards?.find(
        (card: IBuyNowSavedCardResponse) => card.token === selectedCard.token
      );

      if (!savedCard) {
        console.error('Saved card not found');
        dispatch(setIsProcessingPayment(false));
        return;
      }

      const publicKey = paymentMethodsData?.sessionKey?.publicKey;
      if (!publicKey) {
        console.error('Public key not found');
        dispatch(setIsProcessingPayment(false));
        return;
      }

      const paymentPayload = {
        payments: [
          {
            paymentProcessor: savedCard.methodId,
            paymentToken: savedCard.token,
            encryptedCVV: encryptData(data.cvv, publicKey),
            installments: Number(data.fees) || 1
          }
        ]
      };

      const result = await store.dispatch(
        createBuyNowOrderAsync({
          payload: paymentPayload,
          cartId
        })
      );

      if (createBuyNowOrderAsync.fulfilled.match(result)) {
        const urlSearchParams = buildBuyNowPurchaseSearchParams(
          result.payload?.id ?? ''
        );
        const paramsString = urlSearchParams.toString();
        const orderProcessingUrl = `${URL_ROUTES.BILLING_ORDER_PROCESSING_SUCCESS_PATH}?${paramsString}`;
        navigate(orderProcessingUrl, { replace: true });
      } else if (createBuyNowOrderAsync.rejected.match(result)) {
        const errorResponse = result.payload as IErrorResponse;
        handleBuyNowPaymentError(errorResponse);
        dispatch(setIsSummaryOpen(false));
      } else {
        throw new Error('Order creation failed');
      }
    } catch (error) {
      console.error('Error creating order: %s', cartId, error);

      const errorResponse = error as { response?: { data?: IErrorResponse } };
      handleBuyNowPaymentError(errorResponse?.response?.data);

      dispatch(setIsSummaryOpen(false));
    } finally {
      dispatch(setIsProcessingPayment(false));
    }
  };

  const handleError = (errors: FieldErrors<CardPaymentFormFields>): void => {
    console.log('Form submission errors:', errors);
    dispatch(setIsProcessingPayment(false));
    dispatch(setIsSummaryOpen(false));
  };

  const submissionHandler = handleSubmit(handleSubmitForm, handleError);

  const isTermsChecked = useWatch({
    control: control,
    name: 'terms'
  });

  return {
    isTermsChecked,
    submissionHandler,
    paymentForm
  };
};
